// Libraries
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { useHistory } from 'react-router-dom'

// Components
import Text from 'ui/Text'
import Steps from 'ui/Steps'
import Heading from 'ui/Heading'
import Button from 'ui/Button'
import Box from 'ui/Box'
import InputPassword from 'ui/Form/InputPassword'
import ShowInputPassword from 'ui/Form/ShowInputPassword'
import PopupExistMail from './PopupExistMail'
import PopupSocialLogin from './PopupSocialLogin'

// Use
import { useDesktop } from 'use/media'
import { useFormHook } from 'use/form'

// Api
import { register } from 'api/register'

// Paths
import { PATH_FORGOT_PASSWORD, PATH_LOGIN } from 'routes/paths'

// Services
import dialog from 'services/dialog'

// Validation schema
const validationSchema = yup.object().shape({
  password: yup
    .string()
    .matches(
      `^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{9,}$`,
      () => <div />
    )
    .required('The password field is required'),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref('password'), null], `Passwords do not match`)
    .required('The confirm password field is required'),
})

function PageRegisterNew({ vendorEmail }) {
  // State
  const [isLoading, setLoading] = useState(false)

  // Use hook
  const isDesktop = useDesktop()
  const history = useHistory()

  // UseFormHook
  const { form, errors, validate, onChange, onBlur } = useFormHook({
    schema: validationSchema,
    initialForm: {
      password: '',
      passwordConfirm: '',
    },
  })

  const onSubmit = async (e) => {
    e.preventDefault()

    await validate()

    setLoading(true)

    try {
      await register(form, vendorEmail)

      history.push(PATH_LOGIN)
    } catch ({ response }) {
      if (response && response.data) {
        // Email existed
        if (response.data.code === 'SIGNUP.EMAIL.UNIQUE') {
          return dialog.create(PopupExistMail, {
            onClickLogin: () => {
              history.push({
                pathname: PATH_LOGIN,
                search: '?existed=true',
              })
            },
            onClickResetPassword: () => {
              history.push(PATH_FORGOT_PASSWORD)
            },
          })
        }

        // Social login
        if (response.data.code === 'SIGNUP.SOCIAL') {
          return dialog.create(PopupSocialLogin, {
            onClickLogin: () => {
              history.push(PATH_LOGIN)
            },
          })
        }
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box backgroundColor={'transparent'} style={{ minWidth: '70%' }}>
      <form onSubmit={onSubmit}>
        <Heading paddingTop={0} paddingBottom={10}>
          Hi Ka-Globe!
        </Heading>
        <Text size={18} color="#000" paddingBottom={10}>
          Let’s get started by activating your Paytrack account
        </Text>
        <Steps stepsLength={2} current={2} />
        <Text bold color="#000" size={18} paddingTop={10}>
          Set up a password to your account
        </Text>
        <Text
          size={isDesktop ? 16 : 14}
          paddingTop={15}
          paddingBottom={0}
          color="#000000"
          bold
        >
          Password must be a combination of:
        </Text>
        <InputPassword
          value={form.password}
          label="Password"
          name="password"
          placeholder="Enter password here"
          errors={errors}
          onChange={onChange}
          onBlur={onBlur}
          style={{ width: '100%' }}
        />
        <ShowInputPassword
          value={form.passwordConfirm}
          label="Re-type password"
          name="passwordConfirm"
          placeholder="Re-type password here"
          errors={errors}
          onChange={onChange}
          onBlur={onBlur}
          style={{ width: '100%' }}
        />
        <Button
          type={'submit'}
          width={isDesktop ? '250px' : '100%'}
          loading={isLoading}
        >
          Save and login
        </Button>
      </form>
    </Box>
  )
}

PageRegisterNew.propTypes = {
  vendorEmail: PropTypes.string,
}
PageRegisterNew.defaultProps = {
  vendorEmail: '',
}

export default PageRegisterNew
